// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import Latex from '../../Latex.js';
import LatexInternal from '../../LatexInternal.js';
import { INVEmphasis } from './INVEmphasis.js';
import { LatexTextAtom } from '../../LatexTextAtom.js';

export function INVExplainedEquation(props: {|
  children: ChildrenArray<
    | Element<typeof INVExplainedEquationLatex>
    | Element<typeof INVExplainedEquationDescription>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    INVExplainedEquation,
  );

  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;

    const [latex, ...descriptions] = React.Children.toArray(props.children);

    if (latex.type.name !== 'INVExplainedEquationLatex') {
      addErrors([
        {
          message: `The first child must be <INVExplainedEquationLatex>`,
          ...latex.props._sourceLocations?.[String(latex.type.name)],
        },
      ]);
      // Subsequent error messages will be confusing if the 0th child is
      // not correct so we'll bail out early.
      return;
    }

    if (descriptions.length === 0) {
      addErrors([
        {
          message: `There must be at least one <INVExplainedEquationDescription>`,
          ...locations.INVExplainedEquation,
        },
      ]);
    }

    const descriptionErrors = descriptions
      .filter((d) => d.type.name !== 'INVExplainedEquationDescription')
      .map((d) => ({
        message:
          'All children must be <INVExplainedEquationDescription> except for the 1st child (which must be <INVExplainedEquationLatex>)',
        ...d.props._sourceLocations?.[String(d.type.name)],
      }));
    if (descriptionErrors.length > 0) addErrors(descriptionErrors);
  });

  if (failedValidation) return null;

  const [latex, ...descriptions] = React.Children.toArray(props.children);

  return (
    <div style={{ margin: '24px 0', breakInside: 'avoid' }}>
      <div style={{ textAlign: 'center' }}>{latex}</div>
      <div style={{ height: 16 }} />
      <div style={{ display: 'table', margin: '0 auto' }}>{descriptions}</div>
    </div>
  );
}
INVExplainedEquation.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.elementType(INVExplainedEquationLatex),
      t.elementType(INVExplainedEquationDescription),
    ),
  ),
};

export function INVExplainedEquationLatex(props: {|
  expression: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVExplainedEquationLatex,
  );
  if (failedValidation) return null;
  return (
    <LatexInternal
      fontSize={24}
      expression={`\\displaystyle ${props.expression}`}
    />
  );
}
INVExplainedEquationLatex.spec = {
  expression: t.latex,
};

export function INVExplainedEquationDescription(props: {|
  variable: string,
  children: ChildrenArray<
    | string
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof INVEmphasis>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    INVExplainedEquationDescription,
  );
  if (failedValidation) return null;
  return (
    <div style={{ display: 'table-row' }}>
      <div style={{ display: 'table-cell' }}>
        <LatexInternal expression={`\\bm{${props.variable}}`} />
      </div>
      <div style={{ display: 'table-cell', width: 8 }} />
      {/* We want the text to be vaguely centered so we wrap at 20em rather
          than allowing the entire row to be filled with an explanation */}
      <div style={{ display: 'table-cell', maxWidth: '20em' }}>
        {props.children}
      </div>
    </div>
  );
}
INVExplainedEquationDescription.spec = {
  variable: t.latex,
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(INVEmphasis),
    ),
  ),
};
